import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import type { EntityCustomViewParams } from "@rebasepro/cms-types";
import { useSidePanel } from "@rebasepro/cms";
import { useSnackbarController } from "@rebasepro/app";
import {
    Button,
    Card,
    cardClickableMixin,
    Chip,
    cls,
    defaultBorderMixin,
    ExternalLinkIcon,
    iconSize,
    Typography
} from "@rebasepro/ui";
import { inboxPath } from "rebase-leadgen-config";
import { client } from "../lib/rebaseClient";
import { EmailComposer } from "../inbox/EmailComposer";
import { isWaitingOnUs, sinceLabel, useMailboxes, whenInSentence, type Person, type Thread } from "../inbox/shared";
import { EntityEmailPanel } from "./EntityEmailPanel";
import { TasksPanel } from "./TasksPanel";
import { Timeline } from "./Timeline";

/**
 * A company, as the CRM sees it — the first tab its record opens on.
 *
 * The people here are found two ways and merged: anyone whose address is on
 * the company's domain, and anyone whose record names the company. A person's
 * `company` is free text, not a relation, so the second is a match on the name —
 * good enough to surface them, and worth a glance before trusting.
 */

type Row = Record<string, unknown>;
type EnumOption = { id: string; label: string; color?: string };
const str = (value: unknown) => (typeof value === "string" ? value.trim() : "");

export function CompanyOverview({ collection, entity, modifiedValues }: EntityCustomViewParams) {
    const navigate = useNavigate();
    const sidePanel = useSidePanel();
    const snackbar = useSnackbarController();
    const { mailboxes } = useMailboxes();
    const values = (modifiedValues ?? entity?.values ?? {}) as Row;
    const id = entity?.id != null ? String(entity.id) : "";
    const name = str(values.name) || str(values.domain) || "this company";
    const domain = str(values.domain).toLowerCase();

    const [people, setPeople] = useState<Person[]>([]);
    const [waiting, setWaiting] = useState<Thread | null>(null);
    const [composing, setComposing] = useState(false);

    const status = str(values.status) || "new";
    const statusOption = ((collection.properties?.status as { enum?: EnumOption[] } | undefined)?.enum ?? []).find(o => o.id === status);
    const stack = Array.isArray(values.stack) ? (values.stack as string[]).filter(Boolean) : [];

    useEffect(() => {
        if (!id) return;
        let live = true;
        (async () => {
            const found = new Map<string, Person>();
            const queries: Record<string, unknown>[] = [
                ...(domain ? [{ email: ["ilike", `%@${domain}`] }] : []),
                ...(str(values.name) ? [{ company: ["ilike", `%${str(values.name)}%`] }] : [])
            ];
            for (const where of queries) {
                const { data } = await client.data.collection<Person>("prospects").find({ where: where as never, limit: 25 });
                for (const p of data ?? []) found.set(p.id, p);
            }
            const { data: threads } = await client.data.collection<Thread>("email_threads").find({
                where: { companyId: ["==", id] } as never, orderBy: ["last_message_at", "desc"], limit: 20
            });
            if (!live) return;
            setPeople([...found.values()]);
            setWaiting((threads ?? []).find(isWaitingOnUs) ?? null);
        })().catch(() => { /* the cover still shows the company */ });
        return () => { live = false; };
    }, [id, domain, values.name]);

    if (!id) {
        return <Typography variant="body2" color="secondary" className="p-6">Save the company first — then its people, conversations and timeline live here.</Typography>;
    }

    return (
        <div className="flex flex-col gap-6 p-6 max-w-4xl">
            <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3 flex-wrap">
                    <Typography variant="h5">{name}</Typography>
                    <Chip size="small" colorScheme={`${statusOption?.color ?? "gray"}Lighter` as never}>{statusOption?.label ?? status}</Chip>
                    {typeof values.fit_score === "number" && <Chip size="small" colorScheme="blueLighter">fit {values.fit_score}/5</Chip>}
                </div>
                <div className="flex items-center gap-1 flex-wrap -ml-2">
                    {(str(values.website) || domain) && (
                        <Button size="small" variant="text" component="a" href={str(values.website) || `https://${domain}`} target="_blank" rel="noreferrer">
                            <ExternalLinkIcon size={iconSize.smallest} /> {domain || str(values.website)}
                        </Button>
                    )}
                </div>
                {str(values.description) && <Typography variant="body2" color="secondary">{str(values.description)}</Typography>}
                {stack.length > 0 && (
                    <div className="flex gap-1 flex-wrap">
                        <Typography variant="caption" color="secondary" className="mr-1">Runs on</Typography>
                        {stack.map(item => <Chip key={item} size="small" colorScheme="grayLighter">{item}</Chip>)}
                    </div>
                )}
            </div>

            <Card className={cls("p-4 flex flex-col gap-2", defaultBorderMixin)}>
                <div className="flex items-center gap-2">
                    <Typography variant="caption" color="secondary">NEXT UP</Typography>
                    <Chip size="small" colorScheme={waiting ? "orangeLighter" : people.length ? "blueLighter" : "grayLighter"}>
                        {waiting ? "Answer them" : people.length ? "Write to someone there" : "Find someone to talk to"}
                    </Chip>
                </div>
                <Typography variant="body2">
                    {waiting
                        ? `They wrote ${whenInSentence(waiting.last_inbound_at)}${waiting.last_snippet ? `: “${waiting.last_snippet}”` : "."}`
                        : people.length
                            ? `${people.length} ${people.length === 1 ? "person" : "people"} here in People.`
                            : "Nobody from here is in People yet."}
                </Typography>
                <div className="flex gap-2 flex-wrap">
                    {waiting && <Button size="small" variant="filled" color="primary" onClick={() => navigate(inboxPath({ thread: waiting.id }))}>Open the conversation</Button>}
                    {!composing && <Button size="small" variant="outlined" onClick={() => setComposing(true)}>Write to {name}</Button>}
                </div>
            </Card>

            {composing && (
                <EmailComposer
                    target={{ mode: "new", to: [], subject: "", companyId: id, companyName: name }}
                    mailboxes={mailboxes}
                    onCancel={() => setComposing(false)}
                    onSent={async ({ threadId }) => {
                        setComposing(false);
                        snackbar.open({ type: "success", message: "Sent" });
                        navigate(inboxPath({ thread: threadId }));
                    }}
                />
            )}

            {people.length > 0 && (
                <div className="flex flex-col gap-2">
                    <Typography variant="subtitle2">People here ({people.length})</Typography>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {people.map(person => (
                            <Card
                                key={person.id}
                                className={cls("p-3 cursor-pointer", cardClickableMixin, defaultBorderMixin)}
                                onClick={() => sidePanel.open({ path: "prospects", entityId: person.id, selectedTab: "overview" })}
                            >
                                <Typography variant="body2" className="font-medium truncate">{person.display_name || person.handle || person.email}</Typography>
                                <Typography variant="caption" color="secondary" className="block truncate">
                                    {[person.role, person.email].filter(Boolean).join(" · ") || "no address on file"}
                                </Typography>
                            </Card>
                        ))}
                    </div>
                </div>
            )}

            <EntityEmailPanel kind="company" entityId={id} name={name} limit={5} showCompose={false} />
            <TasksPanel companyId={id} />
            <Timeline companyId={id} />
        </div>
    );
}
